import { PART_STATUS_ORDERED, PARTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { Box, LinearProgress, Typography } from "@mui/material";
import { Inventory2, Inventory2Outlined, Sell, Savings } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useHasPermission } from "../../../../contexts/auth";
import { formatDate, formatMoney, formatQuantity } from "../../../../utils";
import {
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
  DetailValue,
  InfoHint,
} from "../../../../components/ui/detailView";
import { StatusChip } from "../../../../components/ui/statusChip/statusChip.component";
import EntityReferenceLink from "../../../../components/ui/entityReference/entityReferenceLink.component";
import { buildOrderedPartReferenceId } from "../../../../utils/entityRoutes";
import { StatCard } from "../../../dashboard/dashboard.component";

/** One batch and its price, as the Part detail endpoint reports it. */
type BatchPrice = {
  ordered_part_id: number;
  number: string | null;
  status: string;
  status_display: string;
  delivered_at: string | null;
  quantity: string;
  availability: string;
  price_net: string;
  vat: string;
  price_gross: string;
};

type StockSummary = {
  ordered_quantity: string;
  used_quantity: string;
  available_quantity: string;
  last_delivered_at: string | null;
  stock_value_net: string;
  stock_value_gross: string;
};

/** How a job invoice prices the part, resolved by the backend (Part.sale_price_for). */
type SalePricing = {
  source: "fixed" | "part_markup" | "default_markup";
  /** The markup that applies - the part's own or the workspace's; null for a fixed price. */
  markup: string | null;
  /** What the next use is invoiced at, net; null when nothing is in stock to price it from. */
  next_issue_price_net: string | null;
};

type Part = {
  id: number;
  name: string;
  code: string | null;
  unit: string;
  stock_quantity: string;
  notification_threshold: string | null;
  is_below_notification_threshold: boolean;
  /** The batch the next use is drawn from (earliest delivery with stock left). */
  current_price: BatchPrice | null;
  /** The latest order not used up yet, possibly still on its way. */
  newest_order: BatchPrice | null;
  sale_pricing: SalePricing;
  stock_summary: StockSummary;
  created_at: string;
  updated_at: string;
};

/** The pricing rule on one line, with what the next use comes to underneath. */
const SalePricingValue = ({ pricing }: { pricing: SalePricing }) => {
  const { t } = useTranslation();
  const markup = formatQuantity(pricing.markup);
  const rule =
    pricing.source === "fixed"
      ? t("Fixed price")
      : pricing.source === "part_markup"
        ? t("Purchase price + {{markup}}% markup", { markup })
        : t("Purchase price + {{markup}}% workspace markup", { markup });

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle1">{rule}</Typography>
      {pricing.next_issue_price_net !== null && (
        <Typography variant="caption" color="text.secondary" component="div">
          {pricing.source === "fixed"
            ? `${formatMoney(pricing.next_issue_price_net)} ${t("Net")}`
            : t("Next use: {{price}} net", { price: formatMoney(pricing.next_issue_price_net) })}
        </Typography>
      )}
    </Box>
  );
};

/** One batch's price, as a stacked block: the net price, its gross underneath,
 * then the order it comes from on its own line.
 *
 * The order link is deliberately not nested inside a caption Typography -
 * EntityReferenceLink sets its own 0.875rem size, so next to 0.75rem caption
 * text it renders visibly out of scale. Giving it its own line at body2 size
 * keeps the two matched. */
const BatchPriceBlock = ({
  heading,
  priceNet,
  priceGross,
  link,
  footnote,
}: {
  heading?: string;
  priceNet: string;
  priceGross: string;
  link: ReactNode;
  footnote?: string;
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
      {heading && (
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4,
          }}
        >
          {heading}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {formatMoney(priceNet)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatMoney(priceGross)} {t("Gross")}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
          fontSize: "0.875rem",
          color: "text.secondary",
        }}
      >
        {link}
        {footnote && <Box component="span">{footnote}</Box>}
      </Box>
    </Box>
  );
};

export const PartBasicInfo = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(PARTS_ENDPOINT);
  const [part, setPart] = useState<Part | null>(null);
  const canViewParts = useHasPermission("service.view_part");
  const canViewOrderedParts = useHasPermission("service.view_orderedpart");

  useEffect(() => {
    const fetchPart = async () => {
      if (id) {
        const response = await get(id);
        setPart(response.data);
      }
    };

    const handlePartUpdated = () => {
      fetchPart();
    };

    // The header's stock, price and value all move when a batch is ordered or
    // used, so refresh on those events too - not just on edits to the part.
    const events = ["partUpdated", "orderedPartAdded", "orderedPartUpdated", "usedPartAdded", "usedPartUpdated"];
    events.forEach((event) => window.addEventListener(event, handlePartUpdated));
    fetchPart();
    return () => {
      events.forEach((event) => window.removeEventListener(event, handlePartUpdated));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const partId = part?.id;
  const renderBatchLink = useCallback(
    (batch: BatchPrice) => (
      <EntityReferenceLink
        entityType="orderedPart"
        entityId={
          partId === undefined
            ? undefined
            : buildOrderedPartReferenceId(partId, batch.ordered_part_id)
        }
        label={batch.number ?? undefined}
        canPreview={canViewOrderedParts}
      />
    ),
    [canViewOrderedParts, partId]
  );

  if (!canViewParts) {
    return null;
  }

  if (!part) return <DetailPage loading />;

  const summary = part.stock_summary;
  const threshold = part.notification_threshold;
  const isLow = part.is_below_notification_threshold;
  const stockValue = Number(part.stock_quantity);
  const thresholdValue = threshold === null ? null : Number(threshold);
  // The newest order is only worth a block of its own when it is not the batch
  // the next use comes from - with a single open order the two are the same
  // row of data, and labelling them separately would repeat it twice.
  const newestOrder =
    part.newest_order !== null &&
    part.newest_order.ordered_part_id !== part.current_price?.ordered_part_id
      ? part.newest_order
      : null;
  const showsTwoBatches = part.current_price !== null && newestOrder !== null;
  // How close the shelf is to the alarm. Pinned at full once stock is clear of
  // the threshold - past that the exact ratio says nothing useful.
  const thresholdProgress =
    thresholdValue && thresholdValue > 0
      ? Math.max(0, Math.min(100, (stockValue / thresholdValue) * 100))
      : null;

  return (
    <DetailPage>
      <DetailHeader
        icon={<Inventory2Outlined />}
        title={part.name}
        subtitle={part.code && `${t("Code")}: ${part.code}`}
        chips={isLow && <StatusChip color="error" label={t("Below notification threshold")} />}
      />

      {/* The three numbers the client asked to see without clicking further. */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", width: "100%" }}>
        <StatCard
          title={t("Stock Quantity")}
          value={(
            <Box sx={{ minWidth: 170 }}>
              <Typography
                variant="h4"
                component="div"
                fontWeight="bold"
                color={isLow ? "error.main" : undefined}
              >
                {`${formatQuantity(part.stock_quantity)} ${part.unit}`}
              </Typography>
              {thresholdProgress !== null ? (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={thresholdProgress}
                    color={isLow ? "error" : "success"}
                    sx={{ flex: 1, height: 8, borderRadius: 999 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {`${t("Notification Threshold")}: ${formatQuantity(threshold)}`}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  {t("No notification threshold set")}
                </Typography>
              )}
            </Box>
          )}
          icon={<Inventory2 />}
          color={isLow ? "#d32f2f" : "#2e7d32"}
        />
        <StatCard
          title={t("Current Price")}
          value={(
            <Box sx={{ minWidth: 170 }}>
              {part.current_price ? (
                <>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {formatMoney(part.current_price.price_net)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {`${formatMoney(part.current_price.price_gross)} ${t("Gross")} (+${formatQuantity(part.current_price.vat)}% ${t("VAT")})`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {`${t("From Order")} ${part.current_price.number ?? "-"}`}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("No delivered order with stock left")}
                </Typography>
              )}
            </Box>
          )}
          icon={<Sell />}
          color="#0288d1"
        />
        <StatCard
          title={t("Stock Value")}
          value={(
            <Box sx={{ minWidth: 170 }}>
              <Typography variant="h4" component="div" fontWeight="bold">
                {formatMoney(summary.stock_value_net)}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {`${formatMoney(summary.stock_value_gross)} ${t("Gross")}`}
              </Typography>
            </Box>
          )}
          icon={<Savings />}
          color="#f57c00"
        />
      </Box>

      <DetailSection title={t("Part Details")}>
        <DetailRow label={t("Code")} value={part.code} />
        <DetailRow label={t("Name")} value={part.name} />
        <DetailRow label={t("Unit")} value={part.unit} />
        <DetailRow
          label={t("Stock Quantity")}
          value={(
            <DetailValue>
              {`${formatQuantity(part.stock_quantity)} ${part.unit}`}
            </DetailValue>
          )}
        />
        <DetailRow
          label={(
            <>
              {t("Notification Threshold")}
              <InfoHint text={t("A notification is sent once stock reaches this level.")} />
            </>
          )}
          value={threshold === null ? null : `${formatQuantity(threshold)} ${part.unit}`}
        />
        <DetailRow
          label={t("Total Ordered")}
          value={(
            <DetailValue>
              {`${formatQuantity(summary.ordered_quantity)} ${part.unit}`}
            </DetailValue>
          )}
        />
        <DetailRow
          label={t("Total Used")}
          value={(
            <DetailValue>
              {`${formatQuantity(summary.used_quantity)} ${part.unit}`}
            </DetailValue>
          )}
        />
        <DetailRow
          label={t("Last Delivery")}
          value={summary.last_delivered_at && formatDate(summary.last_delivered_at)}
        />
        <DetailRow
          label={(
            <>
              {t("Current Price")}
              <InfoHint
                text={t(
                  "The current price is what the next use is charged at: stock is issued oldest delivery first, from the earliest-delivered order still holding stock. The newest order is shown next to it when it is a different one."
                )}
              />
            </>
          )}
          alignTop
          value={
            part.current_price || newestOrder ? (
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", minWidth: 0 }}>
                {part.current_price && (
                  <BatchPriceBlock
                    // Only worth naming which batch is which when there are two.
                    heading={showsTwoBatches ? t("Next issued from") : undefined}
                    priceNet={part.current_price.price_net}
                    priceGross={part.current_price.price_gross}
                    link={renderBatchLink(part.current_price)}
                    footnote={`· ${formatQuantity(part.current_price.availability)} ${part.unit} ${t("left")}`}
                  />
                )}
                {newestOrder && (
                  <BatchPriceBlock
                    heading={t("Newest order")}
                    priceNet={newestOrder.price_net}
                    priceGross={newestOrder.price_gross}
                    link={renderBatchLink(newestOrder)}
                    // An order still on its way has nothing on the shelf yet.
                    footnote={
                      newestOrder.status === PART_STATUS_ORDERED
                        ? `· ${newestOrder.status_display}`
                        : `· ${formatQuantity(newestOrder.availability)} ${part.unit} ${t("left")}`
                    }
                  />
                )}
              </Box>
            ) : null
          }
        />
        <DetailRow
          label={(
            <>
              {t("Price on job invoices")}
              <InfoHint
                text={t(
                  "What a use of this part is billed at when a job is invoiced. The invoice is created as a draft, so the price can still be changed there."
                )}
              />
            </>
          )}
          alignTop
          value={<SalePricingValue pricing={part.sale_pricing} />}
        />
      </DetailSection>

      <DetailMetadata createdAt={part.created_at} updatedAt={part.updated_at} />
    </DetailPage>
  );
};
